import React, { useState, useEffect, useCallback } from "react";
import {
  Space,
  message,
  Table,
  Button,
  Typography,
  Modal,
  Input,
  Select,
} from "antd";
import Util from "../../Util";

const { Title, Text } = Typography;
const { TextArea } = Input;

const PAYMENT_STATUS = {
  PENDING: "PENDING", // New, or re-requested for this month's trf
  SUCCESS: "SUCCESS",
  PAUSED: "PAUSED", // Supplemented by reasons eg. Transaction failed, <100THB
  VOID: "VOID", // Will not be paid due to stuffs like copyright
};

function PendingPayment() {
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [paymentData, setPaymentData] = useState([]);
  const [monthFilters, setMonthFilters] = useState([]);
  const [codeNameFilters, setCodeNameFilters] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [modalRows, setModalRows] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [remarkMessage, setRemarkMessage] = useState("");
  const [newStatus, setNewStatus] = useState(PAYMENT_STATUS.PAUSED);

  const loadPayments = useCallback(async () => {
    setLoading(true);
    const data = await Util.sendGet(
      "/api/moresheetStaff/getMoresheetPendingPayments",
      {}
    );
    if (!data.success) {
      messageApi.error("Error Loading");
      setLoading(false);
      return;
    }
    const stores = data.data.relaventStores;
    const sellers = data.data.relaventSellers;
    const storesMap = new Map(stores.map((st) => [st.code, st]));
    const sellersMap = new Map(
      sellers.map((seller) => [seller.facebookID, seller])
    );
    const payments = data.data.payments.map((pay) => {
      const store = storesMap.get(pay.store);
      const seller = sellersMap.get(pay.author);
      return {
        key: pay._id,
        month: `${pay.year}-${String(pay.month).padStart(2, "0")}`,
        store: store?.name?.[0],
        codeName: seller?.codeName,
        bankInfo: seller?.bankInfo,
        realName: seller?.realName,
        amount: (pay.total * 0.6).toFixed(2),
        status: pay.paymentStatus,
        remark: pay.paymentPausedReason,
        phone: seller?.phone,
        email: seller?.email,
      };
    });
    setPaymentData(payments);
    setMonthFilters(
      [...new Set(payments.map((item) => item.month))].map((month) => ({
        text: month,
        value: month,
      }))
    );
    setCodeNameFilters(
      [...new Set(payments.map((item) => item.codeName))].map((codeName) => ({
        text: codeName,
        value: codeName,
      }))
    );
    setLoading(false);
  }, [messageApi]);

  useEffect(() => {
    const initialFetch = async () => {
      loadPayments();
    };
    initialFetch();
  }, [messageApi, loadPayments]);

  const COLUMNS = [
    {
      title: "เดือน",
      dataIndex: "month",
      key: "month",
      defaultSortOrder: "descend",
      filters: monthFilters,
      filterSearch: true,
      onFilter: (value, record) => record.month === value,
      sorter: (a, b) => {
        if (a.month === b.month) return 0;
        return a.month < b.month ? -1 : 1;
      },
    },
    {
      title: "มหาวิทยาลัยที่ลงชีทขาย",
      dataIndex: "store",
      key: "store",
    },
    {
      title: "นามปากกา",
      dataIndex: "codeName",
      key: "codeName",
      filters: codeNameFilters,
      filterSearch: true,
      onFilter: (value, record) => record.codeName === value,
    },
    {
      title: "ส่วนแบ่งรายได้ 60%",
      dataIndex: "amount",
      key: "amount",
    },
    {
      title: "ข้อมูลธนาคาร",
      dataIndex: "bankInfo",
      key: "bankInfo",
    },
    {
      title: "สถานะ",
      dataIndex: "status",
      key: "status",
      render: (text, _) => {
        if (text === PAYMENT_STATUS.SUCCESS) {
          return (
            <Text strong underline type="success">
              โอนแล้ว
            </Text>
          );
        } else if (text === PAYMENT_STATUS.PENDING) {
          return (
            <Text strong underline type="warning">
              รอโอนเงิน
            </Text>
          );
        } else if (text === PAYMENT_STATUS.PAUSED) {
          return (
            <Text strong underline type="danger">
              ไม่สำเร็จ
            </Text>
          );
        } else {
          return (
            <Text strong underline type="danger">
              รายได้ถูกยกเลิก
            </Text>
          );
        }
      },
    },
    {
      title: "หมายเหตุ",
      dataIndex: "remark",
      key: "remark",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
    },
  ];

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };
  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };
  const hasSelected = selectedRowKeys.length > 0;

  const openModal = () => {
    setModalRows(
      paymentData.filter((row) => selectedRowKeys.includes(row.key))
    );
    setIsModalVisible(true);
  };

  const closeModal = () => {
    setIsModalVisible(false);
  };

  const sendAction = async () => {
    setLoading(true);
    const result = await Util.sendPost(
      "/api/moresheetStaff/updatePaymentStatusByIds",
      {
        ids: selectedRowKeys,
        paymentStatus: newStatus,
        paymentPausedReason: remarkMessage,
      }
    );
    setLoading(false);
    if (result.success) {
      messageApi.success(`สำเร็จ ${result.modifiedCount} รายการ`);
      loadPayments();
      closeModal();
    } else {
      messageApi.error(`ไม่สำเร็จ กรุณาลองใหม่ภายหลัง`);
    }
  };

  const downloadAsCsv = (inpData, inpCol, fileName) => {
    const data = [];
    data.push(inpCol.map((col) => col.title).join(","));
    for (const x of inpData) {
      data.push(inpCol.map((col) => x[col.dataIndex]).join(","));
    }

    const text = data.join("\n");
    const blob = new Blob([text], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Space direction="vertical" size={12}>
      {contextHolder}

      <Modal
        title="อัพเดตสถานะ"
        open={isModalVisible}
        onOk={sendAction}
        onCancel={closeModal}
        okText="บันทึก Action"
      >
        <Title level={5}>สถานะใหม่</Title>
        <Select
          defaultValue={PAYMENT_STATUS.PAUSED}
          style={{ width: 120 }}
          onChange={(val) => setNewStatus(val)}
          options={[
            { value: PAYMENT_STATUS.PAUSED, label: "ไม่สำเร็จ" },
            { value: PAYMENT_STATUS.SUCCESS, label: "สำเร็จ" },
            { value: PAYMENT_STATUS.VOID, label: "ไม่จ่าย" },
            { value: PAYMENT_STATUS.PENDING, label: "ส่งให้ HYTEXT จ่าย" },
          ]}
        />
        <Title level={5}>ใส่หมายเหตุ</Title>
        <TextArea
          rows={5}
          value={remarkMessage}
          onChange={(event) => {
            setRemarkMessage(event.currentTarget.value);
          }}
        ></TextArea>
        <Title level={5}>ข้อมูลที่เลือก</Title>
        <div style={{ width: "100%", overflowX: "scroll" }}>
          <Table dataSource={modalRows} columns={COLUMNS}></Table>
        </div>
      </Modal>

      {loading ? (
        <div>กำลังโหลดข้อมูล...</div>
      ) : (
        <React.Fragment>
          <Title level={4}>รายได้ค้างจ่ายของ บริษัท More Services จำกัด</Title>
          <Button
            type="primary"
            onClick={openModal}
            disabled={!hasSelected}
            loading={loading}
          >
            แก้ไขสถานะ
          </Button>
          {hasSelected ? `เลือกแล้ว ${selectedRowKeys.length} ข้อมูล` : null}
          <Button
            type="primary"
            size="large"
            onClick={() =>
              downloadAsCsv(
                paymentData,
                COLUMNS,
                "pending_payment_more_services.csv"
              )
            }
          >
            Download as CSV
          </Button>
          <Table
            rowSelection={rowSelection}
            dataSource={paymentData}
            columns={COLUMNS}
          ></Table>
        </React.Fragment>
      )}
    </Space>
  );
}

export default PendingPayment;
